import { useState, useEffect } from "react";
import { useNavigate }          from "react-router-dom";
import { apiClient }            from "../../api/apiServices";

const PREFS = [
  {
    group: "Transactions",
    items: [
      { key: "push_transactions",  label: "Transaction Alerts",     sub: "Deposits, withdrawals, payments",   icon: "ri-exchange-line",    tone: "success", default: true  },
      { key: "push_bills",         label: "Bill Payment Status",    sub: "Airtime, data, electricity, TV",    icon: "ri-receipt-line",     tone: "info",     default: true  },
    ],
  },
  {
    group: "Account",
    items: [
      { key: "push_security",      label: "Security Alerts",        sub: "Login attempts and PIN changes",    icon: "ri-shield-line",      tone: "danger",   default: true  },
      { key: "push_kyc",           label: "KYC Updates",            sub: "Verification status changes",       icon: "ri-user-check-line",  tone: "purple",   default: true  },
      { key: "push_referrals",     label: "Referral Activity",      sub: "New referrals and bonuses earned",  icon: "ri-team-line",        tone: "warning",  default: true  },
    ],
  },
  {
    group: "Marketing",
    items: [
      { key: "push_promotions",    label: "Promotions & Offers",    sub: "Cashback, deals, and special offers", icon: "ri-gift-line",      tone: "warning",  default: false },
      { key: "push_tips",          label: "Tips & Features",        sub: "Get the most from Easwallet",         icon: "ri-lightbulb-line", tone: "info",     default: false },
    ],
  },
  {
    group: "Email",
    items: [
      { key: "email_statements",   label: "Monthly Statements",     sub: "Monthly transaction summary",       icon: "ri-mail-line",        tone: "success", default: true  },
      { key: "email_security",     label: "Security Notifications", sub: "Sign-in and account change emails", icon: "ri-mail-lock-line",   tone: "danger",  default: true  },
      { key: "email_promotions",   label: "Promotions",             sub: "Offers and product updates",        icon: "ri-mail-star-line",   tone: "warning", default: false },
    ],
  },
];

function Toggle({ on, onChange }) {
  return (
    <button onClick={() => onChange(!on)}
      className="w-11 h-6 rounded-full transition-all relative flex-shrink-0"
      style={{ background: on ? "var(--color-brand)" : "var(--color-border-strong)" }}>
      <div className="w-4 h-4 rounded-full bg-white absolute top-1 shadow-sm transition-all" style={{ left: on ? "1.5rem" : "0.25rem" }} />
    </button>
  );
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [prefs,  setPrefs]  = useState({});
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);

  useEffect(() => {
    apiClient.get("/mtrade/user/notification-preferences")
      .then(res => { if (res.data.status) setPrefs(res.data.data); })
      .catch(() => {
        const defaults = {};
        PREFS.forEach(g => g.items.forEach(i => { defaults[i.key] = i.default; }));
        setPrefs(defaults);
      });
  }, []);

  const toggle = (key) => setPrefs(prev => ({ ...prev, [key]: !prev[key] }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put("/mtrade/user/notification-preferences", prefs);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch { }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-bg">
      <div className="bg-surface px-5 pt-12 pb-4 border-b border-border flex items-center gap-3">
        <button onClick={() => navigate(-1)}
          className="w-9 h-9 flex items-center justify-center rounded-xl bg-surface-hover hover:opacity-80 transition-colors flex-shrink-0">
          <i className="ri-arrow-left-line text-secondary text-lg" />
        </button>
        <div className="flex-1">
          <h1 className="text-primary font-bold text-lg font-display">
            Notifications
          </h1>
          <p className="text-muted text-xs">Choose what alerts you receive</p>
        </div>
        <button onClick={handleSave} disabled={saving}
          className="bg-brand text-on-brand text-sm font-semibold px-4 py-2 rounded-xl disabled:opacity-50 flex items-center gap-1.5">
          {saving && <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
          {saving ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="px-5 pt-5 pb-12 space-y-5">
        {saved && (
          <div className="flex items-center gap-2 bg-success-light border border-success-border rounded-2xl px-4 py-3">
            <i className="ri-checkbox-circle-fill text-base" style={{ color: "var(--color-success)" }} />
            <p className="text-sm font-medium" style={{ color: "var(--color-success-text)" }}>Preferences saved</p>
          </div>
        )}

        {PREFS.map(group => (
          <div key={group.group}>
            <p className="text-muted text-[11px] font-bold uppercase tracking-widest px-1 mb-2">
              {group.group}
            </p>
            <div className="bg-surface border border-border rounded-2xl overflow-hidden divide-y divide-border">
              {group.items.map(item => {
                const isOn = prefs[item.key] ?? item.default;
                return (
                  <div key={item.key} className="flex items-center gap-3 px-4 py-4">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: `var(--color-${item.tone}-light)` }}>
                      <i className={`${item.icon} text-base`} style={{ color: `var(--color-${item.tone})` }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-secondary text-sm font-medium">{item.label}</p>
                      <p className="text-muted text-xs">{item.sub}</p>
                    </div>
                    <Toggle on={isOn} onChange={() => toggle(item.key)} />
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        <p className="text-muted text-xs text-center px-4">
          Security alerts cannot be turned off as they are required to keep your account safe.
        </p>
      </div>
    </div>
  );
}
